import React, { useRef, useEffect, useState, useCallback } from "react";
import {
  Check,
  X,
  HelpCircle,
  Star,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import type { Slot, VoteType } from "../../lib/insights/types";
import type { PollInsights } from "../../lib/insights/types";
import { groupSlotsByDay } from "../../lib/insights/groupByDay";
import { getCellTone } from "./utils/cellTone";
import { formatDate, formatTime } from "../voting/utils/dateUtils";

export type ResultsMode = "tableau" | "consensus" | "engagement" | "blockers";

export interface Participant {
  /** Stable id (fingerprint or row id) — used for React key */
  id: string;
  /** Display name */
  name: string;
  /** Vote per slotId */
  votes: Record<string, VoteType>;
  /** Whether this row is the current viewer */
  isYou?: boolean;
}

export interface ResultsTableProps {
  mode?: ResultsMode;
  slots: Slot[];
  participants: Participant[];
  insights: PollInsights;
  /** Optional: override the set of slots displayed (consensus mode) */
  visibleSlotIds?: Set<string>;
  /** Slots à mettre en avant (bg-amber tint). Usage : near-misses du mode blockers. */
  highlightedSlotIds?: Set<string>;
  /** Slots à atténuer (30% opacity). Usage : non-near-misses du mode blockers. */
  dimmedSlotIds?: Set<string>;
}

const MOBILE_COLS_PER_PAGE = 5;

const VoteGlyph: React.FC<{ vote: VoteType | null | undefined }> = ({
  vote,
}) => {
  if (vote === "yes") return <Check className="w-3 h-3" aria-hidden />;
  if (vote === "no") return <X className="w-3 h-3" aria-hidden />;
  if (vote === "maybe") return <HelpCircle className="w-3 h-3" aria-hidden />;
  return null;
};

function slotHeaderLabel(slot: Slot): string {
  if (slot.timeSlots && slot.timeSlots.length > 0) {
    return formatTime(slot.timeSlots);
  }
  return formatDate(slot.primaryDate);
}

/** Count "yes" votes a participant has cast on the visible slots */
function countYesVotes(p: Participant, slots: Slot[]): number {
  let n = 0;
  for (const s of slots) if (p.votes[s.id] === "yes") n++;
  return n;
}

/** Counts of yes/maybe/no for a slot */
function slotCounts(
  slotId: string,
  participants: Participant[],
): { yes: number; maybe: number; no: number } {
  let yes = 0,
    maybe = 0,
    no = 0;
  for (const p of participants) {
    const v = p.votes[slotId];
    if (v === "yes") yes++;
    else if (v === "maybe") maybe++;
    else if (v === "no") no++;
  }
  return { yes, maybe, no };
}

/** Winner card shown by default on mobile */
const WinnerCard: React.FC<{
  slot: Slot | undefined;
  counts: { yes: number; maybe: number; no: number } | undefined;
  total: number;
  onExpand: () => void;
}> = ({ slot, counts, total, onExpand }) => {
  const { t } = useTranslation("polls");
  if (!slot || !counts) {
    return (
      <div className="sm:hidden p-4 text-sm text-muted-foreground">
        {t("results.table.no_winner")}
      </div>
    );
  }
  const pct =
    total > 0
      ? Math.round(((counts.yes + counts.maybe * 0.5) / total) * 100)
      : 0;
  return (
    <div className="sm:hidden p-4 space-y-3" data-testid="mobile-winner-card">
      <div className="flex items-center gap-2">
        <Star className="w-4 h-4 text-vote-available-foreground fill-vote-available-foreground" />
        <span className="font-semibold text-vote-available-foreground text-sm">
          {slotHeaderLabel(slot)}
        </span>
        <span className="text-xs text-muted-foreground">
          {counts.yes}/{total} ({pct}%)
        </span>
      </div>
      <div className="flex gap-3 text-xs text-muted-foreground">
        <span className="flex items-center gap-1">
          <Check className="w-3 h-3 text-vote-available-foreground" />
          {counts.yes}
        </span>
        <span className="flex items-center gap-1">
          <HelpCircle className="w-3 h-3 text-vote-maybe-foreground" />
          {counts.maybe}
        </span>
        <span className="flex items-center gap-1">
          <X className="w-3 h-3 text-vote-unavailable-foreground" />
          {counts.no}
        </span>
      </div>
      <button
        onClick={onExpand}
        className="text-xs text-primary underline underline-offset-2"
        data-testid="mobile-expand-table"
      >
        {t("results.table.show_all_slots")}
      </button>
    </div>
  );
};

const ResultsTable: React.FC<ResultsTableProps> = ({
  mode = "tableau",
  slots,
  participants,
  insights,
  visibleSlotIds,
  highlightedSlotIds,
  dimmedSlotIds,
}) => {
  const { t } = useTranslation("polls");
  const visibleSlots = visibleSlotIds
    ? slots.filter((s) => visibleSlotIds.has(s.id))
    : slots;
  const isDimmed = (id: string) => !!dimmedSlotIds && dimmedSlotIds.has(id);
  const isHighlighted = (id: string) =>
    !!highlightedSlotIds && highlightedSlotIds.has(id);
  const winnerSlotId = insights.winnerSlotId;

  // Mobile state
  const [mobileExpanded, setMobileExpanded] = useState(false);
  const [mobilePage, setMobilePage] = useState(0);

  // Reset mobile page when visible slots change (e.g. threshold changes)
  useEffect(() => {
    setMobilePage(0);
  }, [visibleSlots.length]);

  const totalPages = Math.ceil(visibleSlots.length / MOBILE_COLS_PER_PAGE);

  // Scrollbar mirror sync
  const scrollRef = useRef<HTMLDivElement>(null);
  const mirrorRef = useRef<HTMLDivElement>(null);
  const syncing = useRef(false);

  const syncFromScroll = useCallback(() => {
    if (syncing.current || !scrollRef.current || !mirrorRef.current) return;
    syncing.current = true;
    mirrorRef.current.scrollLeft = scrollRef.current.scrollLeft;
    syncing.current = false;
  }, []);

  const syncFromMirror = useCallback(() => {
    if (syncing.current || !scrollRef.current || !mirrorRef.current) return;
    syncing.current = true;
    scrollRef.current.scrollLeft = mirrorRef.current.scrollLeft;
    syncing.current = false;
  }, []);

  useEffect(() => {
    const scroll = scrollRef.current;
    const mirror = mirrorRef.current;
    if (!scroll || !mirror) return;
    scroll.addEventListener("scroll", syncFromScroll);
    mirror.addEventListener("scroll", syncFromMirror);
    return () => {
      scroll.removeEventListener("scroll", syncFromScroll);
      mirror.removeEventListener("scroll", syncFromMirror);
    };
  }, [syncFromScroll, syncFromMirror]);

  // Winner slot info for mobile card
  const winnerSlot = slots.find((s) => s.id === winnerSlotId);
  const winnerCounts = winnerSlotId
    ? slotCounts(winnerSlotId, participants)
    : undefined;

  // Which slots to show in the table body on mobile (paginated) vs desktop (all)
  // We render the full table always but use CSS to hide/show rows per mobile state.
  // For column pagination, we need a JS-side slice because CSS-only column hiding is complex.
  // We use a media-aware approach: track isMobile with matchMedia.
  const [isMobile, setIsMobile] = useState(
    () => window.matchMedia?.("(max-width: 639px)")?.matches ?? false,
  );
  useEffect(() => {
    const mq = window.matchMedia?.("(max-width: 639px)");
    if (!mq) return;
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  // Slots displayed in the table: paginated on mobile-expanded, full on desktop
  const tableSlots =
    isMobile && mobileExpanded
      ? visibleSlots.slice(
          mobilePage * MOBILE_COLS_PER_PAGE,
          (mobilePage + 1) * MOBILE_COLS_PER_PAGE,
        )
      : isMobile
        ? []
        : visibleSlots;

  const dayGroups = groupSlotsByDay(tableSlots);
  const tableMinWidth = 160 + tableSlots.length * 54;

  return (
    <div
      className="bg-card rounded-2xl border border-border overflow-hidden"
      data-testid="results-table"
      data-mode={mode}
    >
      {/* Mobile winner card (hidden on sm+) */}
      {!mobileExpanded && (
        <WinnerCard
          slot={winnerSlot}
          counts={winnerCounts}
          total={insights.participantsCount}
          onExpand={() => setMobileExpanded(true)}
        />
      )}

      {/* Table: hidden on mobile until expanded */}
      <div className={isMobile && !mobileExpanded ? "hidden" : undefined}>
        {/* Mobile pagination controls */}
        {isMobile && mobileExpanded && (
          <div
            className="flex items-center justify-between px-3 py-2 border-b border-border"
            data-testid="mobile-pagination"
          >
            <button
              onClick={() => setMobileExpanded(false)}
              className="text-xs text-muted-foreground underline underline-offset-2"
            >
              ← {t("results.table.back")}
            </button>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setMobilePage((p) => Math.max(0, p - 1))}
                disabled={mobilePage === 0}
                className="rounded p-1 disabled:opacity-30 hover:bg-muted"
                aria-label={t("results.table.previous_page")}
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-xs text-muted-foreground">
                {mobilePage + 1} / {totalPages}
              </span>
              <button
                onClick={() =>
                  setMobilePage((p) => Math.min(totalPages - 1, p + 1))
                }
                disabled={mobilePage >= totalPages - 1}
                className="rounded p-1 disabled:opacity-30 hover:bg-muted"
                aria-label={t("results.table.next_page")}
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Scrollbar mirror (top) — desktop only, hidden on mobile where we paginate */}
        {!isMobile && (
          <div
            ref={mirrorRef}
            className="overflow-x-auto"
            style={{ height: 12 }}
            aria-hidden
          >
            <div style={{ width: tableMinWidth, height: 1 }} />
          </div>
        )}

        {/* Main scroll container */}
        <div ref={scrollRef} className="overflow-x-auto">
          <table
            className="border-collapse text-sm"
            style={{ minWidth: tableMinWidth }}
          >
            <thead>
              {/* Day-banner row */}
              <tr data-testid="results-day-row">
                <th
                  scope="col"
                  className="sticky left-0 z-10 bg-card text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground px-3 py-1 min-w-[160px] max-w-[220px]"
                >
                  <span className="sr-only">
                    {t("results.table.participant")}
                  </span>
                </th>
                {dayGroups.map((g) => (
                  <th
                    key={g.key}
                    colSpan={g.span}
                    scope="col"
                    data-testid={`day-banner-${g.key}`}
                    className="text-center text-xs font-semibold uppercase tracking-wide text-muted-foreground px-2 py-1 border-l border-border/60"
                  >
                    {/^\d{4}-\d{2}-\d{2}$/.test(g.label)
                      ? formatDate(g.label)
                      : g.label}
                  </th>
                ))}
              </tr>
              {/* Slot header row */}
              <tr data-testid="results-slot-row">
                <th
                  scope="col"
                  className="sticky left-0 z-10 bg-card text-left text-xs font-semibold text-foreground px-3 py-2 min-w-[160px] max-w-[220px] border-b border-border"
                >
                  {t("results.table.participant")}
                </th>
                {tableSlots.map((s) => {
                  const isWinner = s.id === winnerSlotId;
                  const dimmed = isDimmed(s.id);
                  const highlighted = isHighlighted(s.id);
                  return (
                    <th
                      key={s.id}
                      scope="col"
                      data-testid={`slot-header-${s.id}`}
                      data-winner={isWinner ? "true" : undefined}
                      data-dimmed={dimmed ? "true" : undefined}
                      data-highlighted={highlighted ? "true" : undefined}
                      className={`text-center text-xs font-medium px-1 py-2 border-b border-border w-[54px] transition-opacity ${
                        isWinner
                          ? "bg-vote-available/[0.04] text-vote-available-foreground"
                          : highlighted
                            ? "bg-warning/10 text-warning"
                            : "text-foreground"
                      } ${dimmed ? "opacity-30" : ""}`}
                    >
                      <div className="flex flex-col items-center gap-0.5">
                        {isWinner && (
                          <Star
                            className="w-3 h-3 text-vote-available-foreground fill-vote-available-foreground"
                            aria-label={t("results.table.winner_slot")}
                            data-testid="winner-star"
                          />
                        )}
                        <span className="truncate max-w-[52px]">
                          {slotHeaderLabel(s)}
                        </span>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {participants.map((p) => (
                <tr
                  key={p.id}
                  data-testid={`participant-row-${p.id}`}
                  data-you={p.isYou ? "true" : undefined}
                  className={p.isYou ? "bg-primary/[0.06]" : undefined}
                >
                  <th
                    scope="row"
                    className="sticky left-0 z-10 bg-card text-left text-sm font-medium text-foreground px-3 py-2 min-min-w-[160px] max-w-[220px] max-w-[220px] border-b border-border/60"
                    title={p.name}
                  >
                    <div className="flex flex-col gap-1 min-w-0">
                      <span className="truncate">{p.name}</span>
                      {mode === "engagement" && tableSlots.length > 0 && (
                        <div
                          data-testid={`engagement-bar-${p.id}`}
                          className="flex items-center gap-1.5"
                          title={t("results.table.availabilities_summary", {
                            yes: countYesVotes(p, tableSlots),
                            total: tableSlots.length,
                          })}
                        >
                          <div
                            className="h-[5px] flex-1 rounded-full bg-muted/50 overflow-hidden"
                            aria-label={t("results.table.engagement_label", {
                              yes: countYesVotes(p, tableSlots),
                              total: tableSlots.length,
                            })}
                          >
                            <div
                              className="h-full bg-vote-available transition-all"
                              style={{
                                width: `${Math.round((countYesVotes(p, tableSlots) / tableSlots.length) * 100)}%`,
                              }}
                            />
                          </div>
                          <span className="text-[10px] tabular-nums text-muted-foreground font-medium shrink-0">
                            {countYesVotes(p, tableSlots)}/{tableSlots.length}
                          </span>
                        </div>
                      )}
                    </div>
                  </th>
                  {tableSlots.map((s) => {
                    const vote = p.votes[s.id];
                    const tone = getCellTone(vote);
                    const isWinner = s.id === winnerSlotId;
                    const dimmed = isDimmed(s.id);
                    const highlighted = isHighlighted(s.id);
                    return (
                      <td
                        key={s.id}
                        data-testid={`cell-${p.id}-${s.id}`}
                        data-vote={vote ?? "empty"}
                        title={tone.title}
                        className={`px-1 py-1 border-b border-border/60 transition-opacity ${
                          isWinner
                            ? "bg-vote-available/[0.04]"
                            : highlighted
                              ? "bg-warning/10"
                              : ""
                        } ${dimmed ? "opacity-30" : ""}`}
                      >
                        <div
                          className={`mx-auto w-[22px] h-[22px] rounded-md flex items-center justify-center ${tone.bgClass} ${tone.fgClass}`}
                        >
                          {vote ? (
                            <VoteGlyph vote={vote} />
                          ) : (
                            <span
                              aria-hidden
                              className="block h-[3px] w-[3px] rounded-full bg-muted-foreground/40"
                            />
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
            <tfoot>
              {/* Score row — handoff: rail 2px vert en haut sur winner col */}
              <tr data-testid="results-score-row">
                <th
                  scope="row"
                  className="sticky left-0 z-10 bg-muted/30 text-left text-[10px] uppercase tracking-wide font-semibold text-muted-foreground px-3 py-1.5 min-w-[160px] max-w-[220px] border-t border-border"
                >
                  {t("results.table.scores")}
                </th>
                {tableSlots.map((s) => {
                  const isWinner = s.id === winnerSlotId;
                  const c = slotCounts(s.id, participants);
                  const dimmed = isDimmed(s.id);
                  const highlighted = isHighlighted(s.id);
                  return (
                    <td
                      key={s.id}
                      data-testid={`score-${s.id}`}
                      className={`relative text-center px-1 py-1.5 border-t border-border transition-opacity ${
                        isWinner
                          ? "bg-vote-available/[0.12]"
                          : highlighted
                            ? "bg-warning/15"
                            : "bg-muted/30"
                      } ${dimmed ? "opacity-30" : ""}`}
                    >
                      {isWinner && (
                        <div
                          className="absolute inset-x-0 top-0 h-[2px] bg-vote-available"
                          aria-hidden
                        />
                      )}
                      <div className="flex flex-col items-center gap-0.5 text-[10px] leading-tight tabular-nums">
                        <span className="text-vote-available-foreground font-medium">
                          {c.yes}
                        </span>
                        <span className="text-vote-maybe-foreground">
                          {c.maybe}
                        </span>
                        <span className="text-vote-unavailable-foreground">
                          {c.no}
                        </span>
                      </div>
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ResultsTable;
